import { useCallback, useState } from 'react';
import { Field, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { connect } from 'react-redux';

import { Row } from 'UIPackage/FormModule/components/Row';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { getFieldArrayErrorMessage } from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { PM_RESULT_OF_INSPECTION } from 'CorePackage/MainModule/routes/routes';
import { KnmViolatedRequirementsNpaItem } from 'KnmPackage/KnmViolatedRequirementsNpa/components/KnmViolatedRequirementsNpaItem';
import { KnmViolatedRequirementsNpaModal } from 'KnmPackage/KnmViolatedRequirementsNpa/components/KnmViolatedRequirementsNpaModal';
import { IKnmErknmFormRequirementNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRequirementNpa';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import {
  IKnmErknmFormViolatedRequirementsMandatory,
  IKnmErknmFormViolatedRequirementsNpa,
} from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataResigningRequired,
  getKnmFormRef,
  getKnmStatusCode,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IRequirementsNpaInitValues } from 'KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal';
import { IKnmPmFormRequirementsNpa } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormRequirementsNpa';
import { Button } from 'UIPackage/MainModule/components/Button';
import {
  getKnmPmResultDecisionsEditEnabled,
  getKnmPmWithoutIssueEditEnabled,
} from 'KnmPmPackage/MainModule/selectors/knmPmSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { KnmErknmStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { StructureRequirementsNpaModal } from 'KnmPackage/StructureRequirementsNpaModule/StructureRequirementsNpaModal';
import { KnmLateFieldsRemark } from 'KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';

import styles from './PmResultOfInspectionItem.module.scss';
import { PmResultOfInspectionPmResultDecisions } from './PmResultOfInspectionPmResultDecisions';
import { PmResultOfInspectionItemResultOfInspection } from './PmResultOfInspectionItemResultOfInspection';
import { IKnmErknmFormObject } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { IKnmFormRiskIndicators } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRiskIndicators';

interface IPmResultOfInspectionItemProps {
  className?: string;
  watchOnly: boolean;
  withoutIssue?: boolean;
  isUnableToRefuse?: boolean;
  pmSubject?: IKnmErknmFormSubject;
  objectsPm?: IKnmErknmFormObject[];
  riskIndicators?: IKnmFormRiskIndicators[];
  hasResultDecisions: boolean;
  knoOrganization: IDictionaryKnoOrganizationSelectOption | undefined;
  supervisionType: IDictionarySupervisionTypeSelectOption | undefined;
  isViolatedMandatoryRequirements: IBooleanSelectOption | undefined;
  requirementsNpa: IKnmPmFormRequirementsNpa[] | undefined;
  values: IKnmPmForm;
}

interface IPmResultOfInspectionItemStoreProps {
  formRef: IKnmFormRef | undefined;
  isResultDecisionsEditEnabled: boolean;
  isWithoutIssueEditEnabled: boolean;
  statusCode: IKnmStatus['code'] | undefined;
  isSupervisor: boolean;
  resigningRequired: boolean;
}

export const PmResultOfInspectionItemWithoutStore = ({
  className = '',
  watchOnly,
  formRef,
  knoOrganization,
  supervisionType,
  requirementsNpa,
  isViolatedMandatoryRequirements,
  pmSubject,
  objectsPm,
  riskIndicators,
  hasResultDecisions,
  withoutIssue,
  isUnableToRefuse,
  isResultDecisionsEditEnabled,
  isWithoutIssueEditEnabled,
  statusCode,
  isSupervisor,
  resigningRequired,
  values,
}: IPmResultOfInspectionItemProps & IPmResultOfInspectionItemStoreProps) => {
  const isPmStatusWaitingCarryOut = statusCode === KnmErknmStatusCodesEnum.TYPE_WAITING_CARRY_OUT;

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const removeViolatedRequirementsNpa = (npaIndex: number) => {
    remove && remove('violatedMandatoryRequirements', npaIndex);
  };

  const [isViolatedRequirementsNpaModalVisible, setIsViolatedRequirementsNpaModalVisible] = useState(false);

  const openViolatedRequirementsNpaModal = useCallback(() => {
    setIsViolatedRequirementsNpaModalVisible(true);
  }, []);

  // Поле "Акт" в ПМ не должно быть заполнено, если мы попадаем в этот раздел
  // костылем сбрасываем его, если вдруг приехало заполненное
  if (values?.act) {
    formRef?.current.change('act');
  }

  const addViolatedRequirementsNpa = (items: IKnmErknmFormRequirementNpa[]) => {
    push &&
      items.forEach(item => {
        const newItem: IKnmErknmFormViolatedRequirementsNpa = {
          guid: item.guid,
          npa: item.npa,
          structuralUnit: item.structuralUnit,
          violatedMandatory: [],
          isNew: true,
        };

        push('violatedMandatoryRequirements', newItem);
      });
  };

  const [isRequirementsModalVisible, setIsRequirementsModalVisible] = useState(false);
  const [initialRequirementsModal, setInitialRequirementsModal] = useState<IRequirementsNpaInitValues | undefined>();
  const [violatedMandatoryFieldName, setViolatedMandatoryFieldName] = useState('');

  const openRequirementsModal = (initialValue: IRequirementsNpaInitValues, fieldName: string) => {
    setInitialRequirementsModal(initialValue);
    setViolatedMandatoryFieldName(fieldName);
    setIsRequirementsModalVisible(true);
  };

  const addViolatedMandatory = (items: IKnmPmFormRequirementsNpa[]) => {
    items.length === 1 &&
      push &&
      items[0].mandatory?.forEach(item => {
        const newItem: IKnmErknmFormViolatedRequirementsMandatory = {
          mandatory: item,
          category: undefined,
          violations: [],
        };

        push(violatedMandatoryFieldName, newItem);
      });
  };

  const handleIsViolatedMandatoryRequirementsChange = useCallback(
    (v: IBooleanSelectOption) => {
      if (!v.value) {
        formRef?.current.change('violatedMandatoryRequirements' as keyof IKnmForm);
      }
    },
    [formRef],
  );

  const handleChangeWithoutIssue = useCallback(() => {
    formRef?.current.change('pmResultDecisions');
  }, []);

  return (
    <>
      <div className={className ? `${styles.Wrapper} ${className}` : styles.Wrapper} id={PM_RESULT_OF_INSPECTION}>
        <h2 className="shared-title__with-margin">Сведения о результатах профилактического мероприятия</h2>
        <PmResultOfInspectionItemResultOfInspection watchOnly={watchOnly} />
        {isUnableToRefuse && (
          <>
            <FieldBlock
              label="Выявлены нарушения обязательных требований"
              size="half"
              id="isViolatedMandatoryRequirements"
              tooltip={<KnmLateFieldsRemark lateFieldPath="/isViolations" fieldId="isViolatedMandatoryRequirements" />}
            >
              <Row>
                <SelectInputField
                  className="shared-row-flex"
                  name={'isViolatedMandatoryRequirements'}
                  options={booleanSelectOptions}
                  isClearable={false}
                  disabled={watchOnly || !isPmStatusWaitingCarryOut || !isSupervisor || resigningRequired}
                  onChange={handleIsViolatedMandatoryRequirementsChange}
                  size="medium"
                />
                {isViolatedMandatoryRequirements?.value === true && (
                  <Button
                    className="shared-btn-add"
                    text="Добавить"
                    type="button"
                    variant="link"
                    color="primary"
                    disabled={watchOnly || !isPmStatusWaitingCarryOut || !isSupervisor || resigningRequired}
                    onClick={openViolatedRequirementsNpaModal}
                  />
                )}
              </Row>
            </FieldBlock>

            <FormSpy subscription={{ errors: true, touched: true }}>
              {props => {
                const fieldError = getFieldArrayErrorMessage(props.errors?.violatedMandatoryRequirements);
                const isFieldTouched = props.touched?.places;

                if (isFieldTouched && fieldError) {
                  return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
                }

                return null;
              }}
            </FormSpy>
            <FieldArray name="violatedMandatoryRequirements">
              {({ fields }) =>
                fields.map((npaName, npaIndex) => {
                  const fieldValue: IKnmErknmFormViolatedRequirementsNpa = fields.value?.[npaIndex];

                  return (
                    <Field key={npaName} name={npaName}>
                      {({ meta }) => {
                        const isFieldTouched = meta.touched;
                        const errorField = meta.error;

                        return (
                          <KnmViolatedRequirementsNpaItem
                            value={fieldValue}
                            name={npaName}
                            watchOnly={watchOnly || !isPmStatusWaitingCarryOut || !isSupervisor || resigningRequired}
                            isViolationFieldsWatchOnly={watchOnly || !isPmStatusWaitingCarryOut || !isSupervisor}
                            onRemove={() => removeViolatedRequirementsNpa(npaIndex)}
                            addMandatory={openRequirementsModal}
                            touched={isFieldTouched}
                            error={errorField}
                            formRef={formRef}
                            isMandatoryDisabled={false}
                          />
                        );
                      }}
                    </Field>
                  );
                })
              }
            </FieldArray>
            <FieldBlock margin="top">
              <CheckboxField
                id="withoutIssue"
                name="withoutIssue"
                label="Завершение без предписания"
                theme="system"
                type="checkbox"
                size="medium"
                labelClassName="shared-field-label"
                disabled={watchOnly || hasResultDecisions || !isWithoutIssueEditEnabled || resigningRequired}
                onChange={handleChangeWithoutIssue}
              />
            </FieldBlock>
            <FieldBlock margin="top">
              <PmResultOfInspectionPmResultDecisions
                watchOnly={watchOnly || !isResultDecisionsEditEnabled}
                withoutIssue={withoutIssue}
                pmSubject={pmSubject}
                clearWatchOnly={false}
                resigningRequired={resigningRequired}
                values={values}
              />
            </FieldBlock>
          </>
        )}
      </div>
      <KnmViolatedRequirementsNpaModal
        requirementsNpa={requirementsNpa}
        visible={isViolatedRequirementsNpaModalVisible}
        onClose={() => setIsViolatedRequirementsNpaModalVisible(false)}
        onSubmit={addViolatedRequirementsNpa}
      />
      {isRequirementsModalVisible && (
        <StructureRequirementsNpaModal
          onClose={() => setIsRequirementsModalVisible(false)}
          knoVersionId={knoOrganization?.value.dictVersionId || ''}
          supervisionVersionId={supervisionType?.value.dictVersionId || ''}
          objects={objectsPm}
          riskIndicators={riskIndicators}
          withRequirements={true}
          addRequirementsNpa={addViolatedMandatory}
          initValues={initialRequirementsModal}
          inputRequirementsNpa={requirementsNpa}
          npasAlreadyFilled={true}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IPmResultOfInspectionItemStoreProps => ({
  formRef: getKnmFormRef(state),
  isResultDecisionsEditEnabled: getKnmPmResultDecisionsEditEnabled(state),
  isWithoutIssueEditEnabled: getKnmPmWithoutIssueEditEnabled(state),
  statusCode: getKnmStatusCode(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  resigningRequired: getKnmDataResigningRequired(state),
});

export const PmResultOfInspectionItem = connect(mapStateToProps)(PmResultOfInspectionItemWithoutStore);
